// utils/discountUtils.js - Utility functions for discount/sales handling
import { getLocalizedProductName } from "./multilingualUtils";

/**
 * Get simplified variant display name from sales discount record
 * @param {object} discount - Discount/sale record
 * @returns {string} Display name for the variant
 */
export const getVariantDisplayFromDiscount = (discount) => {
    if (!discount.variantId) return null;

    // Use the simplified variantDisplayName if available
    if (discount.variantDisplayName) {
        return discount.variantDisplayName;
    }

    // Fallback to generic variant identifier
    return `Variant ${discount.variantId}`;
};

/**
 * Get localized product name from sales record
 * @param {object} discount - Discount/sale record
 * @param {string} language - Language code
 * @returns {string} Localized product name
 */
export const getProductNameFromDiscount = (discount, language = 'en') => {
    if (!discount.productName) return 'Product';

    return getLocalizedProductName({ name: discount.productName }, language);
};

/**
 * Format discount label for display
 * @param {object} discount - Discount record
 * @param {string} currency - Currency symbol
 * @returns {string} Formatted discount label
 */
export const formatDiscountLabel = (discount, currency = 'AED') => {
    if (!discount) return '';

    return discount.discountType === "percentage"
        ? `${discount.discountValue}% OFF`
        : `${discount.discountValue} ${currency} OFF`;
};

/**
 * Calculate final discounted price
 * @param {number} originalPrice - Original price
 * @param {object} discount - Discount record
 * @returns {number} Discounted price
 */
export const calculateFinalPrice = (originalPrice, discount) => {
    if (!discount) return originalPrice;

    const price = parseFloat(originalPrice) || 0;
    const discountValue = parseFloat(discount.discountValue) || 0;

    if (discount.discountType === "percentage") {
        return price * (1 - discountValue / 100);
    } else {
        return Math.max(0, price - discountValue);
    }
};

/**
 * Check if a discount is currently active based on dates
 * @param {object} discount - Discount record
 * @returns {boolean} Whether discount is active
 */
export const isDiscountActive = (discount) => {
    if (!discount || !discount.isActive) return false;

    const now = new Date();
    const startDate = discount.startDate ? new Date(discount.startDate) : null;
    const endDate = discount.endDate ? new Date(discount.endDate) : null;

    const isStarted = !startDate || startDate <= now;
    const isNotExpired = !endDate || endDate > now;

    return isStarted && isNotExpired;
};

/**
 * Group discounts by type (product vs variant)
 * @param {array} discounts - Array of discount records
 * @returns {object} Grouped discounts
 */
export const groupDiscountsByType = (discounts = []) => {
    return {
        productDiscounts: discounts.filter(d => !d.variantId),
        variantDiscounts: discounts.filter(d => d.variantId)
    };
};
